import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, Validators, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { Location, CommonModule } from '@angular/common';
import { MatSliderModule } from '@angular/material/slider';
import { RouterModule } from '@angular/router';

import { AppService } from '../../app.service';
import { AuthService } from '../../auth.service';
import { CriteriaService } from '../../criteria.service';
import { HomeService } from '../../home/home.service';
import { LoaderService } from '../../loader.service';
import { LayoutService } from '../../layout.service';

import { LoaderComponent } from '../../shared/loader/loader.component';
import { RangeInputComponent } from '../../shared/range-input/range-input.component';
import { CityInputComponent } from '../../shared/city-input/city-input.component';
import { TopButtonComponent } from '../../shared/top-button/top-button.component';
import { GoBackButtonComponent } from '../../shared/go-back-button/go-back-button.component';

import { City } from '../../shared/interfaces/city';
import { User } from '../../shared/interfaces/user';
import { Criteria } from '../../shared/interfaces/criteria';

@Component({
  selector: 'app-my-dating-criteria',
  standalone: true,
  imports: [
    LoaderComponent, 
    FormsModule, 
    ReactiveFormsModule, 
    RangeInputComponent, 
    CityInputComponent,
    TopButtonComponent, 
    GoBackButtonComponent, 
    CommonModule, 
    MatSliderModule,
    RouterModule
  ],
  templateUrl: './my-dating-criteria.component.html',
  styleUrl: './my-dating-criteria.component.scss'
})
export class MyDatingCriteriaComponent implements OnInit, OnDestroy {
  @ViewChild('top') top!: ElementRef

  loading$ = this.loaderService.loading$
  datingCriteriaFormGroup!: FormGroup
  error = false
  user!: User | null
  userSex = ''
  userCityId = ''
  distanceValue: number | null = null
  criteria!: Criteria
  ageRange: {min: number | null, max: number | null} = {min: null, max: null}
  successMessage: string | null = null
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop
  selectedCity: City | null = null

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private criteriaService: CriteriaService,
    private location: Location,
    private homeService: HomeService,
    private loaderService: LoaderService,
    private layoutService: LayoutService
  ){}

  ngOnInit(){
    this.loaderService.show()
    // this.userSex = this.authService.get('sex')
    this.user = JSON.parse(this.authService.get('user'))

    if(!this.user) {
      this.authService.authMe.pipe(takeUntil(this._destroyed)).subscribe(res => {
        this.user = res
      })
    }

    this.datingCriteriaFormGroup = new FormGroup({
      //attractionCtrl: new FormControl(this.user?.sex === 'man' ? Gender.woman : Gender.man, [Validators.required]),
      rangeCtrl: new FormGroup({
        minAgeCtrl: new FormControl(this.ageRange.min, [Validators.required]), 
        maxAgeCtrl: new FormControl(this.ageRange.max, [Validators.required]), 
      }),
      locationCtrl: new FormGroup({
        cityCtrl: new FormControl(this.user?.city?.id || 0, [Validators.required])
      }),
      distanceCtrl: new FormControl(this.distanceValue, [Validators.required]),
    });

    this.criteriaService.getCriteria().pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.criteria = res

      //this.datingCriteriaFormGroup.controls['attractionCtrl'].patchValue(this.criteria.sex)
      this.datingCriteriaFormGroup.controls['rangeCtrl'].get('minAgeCtrl')?.patchValue(this.criteria.age_start)
      this.datingCriteriaFormGroup.controls['rangeCtrl'].get('maxAgeCtrl')?.patchValue(this.criteria.age_end)
      this.datingCriteriaFormGroup.controls['locationCtrl'].get('cityCtrl')?.patchValue(this.criteria.city.id)
      this.datingCriteriaFormGroup.controls['distanceCtrl'].patchValue(this.criteria.distance)

      this.ageRange = { min: this.criteria.age_start || 24, max: this.criteria.age_end || 40}
      this.distanceValue = this.criteria.distance || 50

      this.loaderService.hide()
    })

    this.successMessage = null
  }

  goBack(){
    if(this.isDesktop()) {
      this.location.back()
    }

    this.homeService.pageDisplayed.next('')
  }

  getScrollRequest(request: string){
    if(request === 'scrollToTop') this.top.nativeElement.scrollIntoView()
  }

  formatLabel(value: number): string {
    return `${value}\xa0km` // unbreakable space
  }

  onSubmit(){
    this.loaderService.show()
    const data = this.datingCriteriaFormGroup.value

    data.city_id = this.selectedCity?.id || this.criteria.city.id
    
    this.criteriaService.setCriteria(data).pipe(
      takeUntil(this._destroyed),
      switchMap(() => this.criteriaService.getCriteria())
    ).subscribe(() => {
      this.loaderService.hide()
      this.successMessage = $localize `:Success message for dating criteria edition form@@CRITERIA_CHANGE_OK:Your dating criteria have been updated.`
    
      setTimeout(() => {
        this.successMessage = null
      }, 3000)
    })
  }
  
  getCitySet(city: City | null){
    if(!city) this.datingCriteriaFormGroup.controls['locationCtrl'].get('cityCtrl')?.patchValue(null)
    else {
      this.selectedCity = city
      this.datingCriteriaFormGroup.controls['locationCtrl'].get('cityCtrl')?.patchValue(city.name)
    }
  }

  getDistanceValue(e: number){
    this.distanceValue = e
  }

  getMinValueSelection($event: number){
    this.datingCriteriaFormGroup.controls['minAgeCtrl'].setValue($event)
  }

  getMaxValueSelection($event: number){
    this.datingCriteriaFormGroup.controls['maxAgeCtrl'].setValue($event)
  }

  // getAttraction($event: Gender.man | Gender.woman){
  //   this.datingCriteriaFormGroup.controls['attractionCtrl'].setValue($event)
  // }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}
